/**
 * Firebase-optimized logger with environment-based configuration
 * Integrates with Firebase Analytics and provides structured logging
 */

import {
  getEnvironment,
  isDevelopment,
  isProduction,
  isStaging,
} from "./environment";

// Firebase Analytics types (if available)
interface FirebaseAnalytics {
  logEvent: (eventName: string, parameters?: Record<string, any>) => void;
}

interface LogLevel {
  DEBUG: 0;
  INFO: 1;
  WARN: 2;
  ERROR: 3;
}

const LOG_LEVELS: LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

interface LogEntry {
  timestamp: string;
  level: keyof LogLevel;
  message: string;
  data?: any;
  environment: string;
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, any>;
}

class FirebaseLogger {
  private currentLevel: number;
  private firebaseAnalytics?: FirebaseAnalytics;
  private sessionId: string;
  private userId?: string;
  private logBuffer: LogEntry[] = [];
  private maxBufferSize = 100;

  constructor() {
    this.currentLevel = this.getLogLevelForEnvironment();
    this.sessionId = this.generateSessionId();
    this.initializeFirebaseAnalytics();
  }

  private getLogLevelForEnvironment(): number {
    if (isDevelopment()) return LOG_LEVELS.DEBUG;
    if (isStaging()) return LOG_LEVELS.INFO;
    if (isProduction()) return LOG_LEVELS.WARN;
    return LOG_LEVELS.INFO;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async initializeFirebaseAnalytics() {
    try {
      // Dynamically import Firebase app to check availability
      const { getApp } = await import("firebase/app");

      const app = getApp();
      if (app) {
        // Create a wrapper that matches our interface
        this.firebaseAnalytics = {
          logEvent: (eventName: string, parameters?: Record<string, any>) => {
            // We'll need to get the analytics instance to call logEvent properly
            // For now, we'll just log to console in production
            if (isProduction()) {
              console.log(`[FIREBASE_ANALYTICS] ${eventName}:`, parameters);
            }
          },
        };
      }
    } catch (error) {
      // Firebase Analytics not available, continue without it
      this.debug("Firebase Analytics not available", { error });
    }
  }

  private shouldLog(level: keyof LogLevel): boolean {
    return LOG_LEVELS[level] >= this.currentLevel;
  }

  private createLogEntry(
    level: keyof LogLevel,
    message: string,
    data?: any,
    metadata?: Record<string, any>
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      environment: getEnvironment(),
      userId: this.userId,
      sessionId: this.sessionId,
      metadata,
    };
  }

  private formatLogEntry(entry: LogEntry): string {
    const { timestamp, level, message, environment, sessionId } = entry;
    return `[${timestamp}] [${level.toUpperCase()}] [${environment.toUpperCase()}] [${sessionId}] ${message}`;
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  private async sendToFirebase(entry: LogEntry) {
    if (!this.firebaseAnalytics || !isProduction()) return;

    try {
      // Send structured log to Firebase Analytics
      this.firebaseAnalytics.logEvent("app_log", {
        level: entry.level,
        message: entry.message,
        environment: entry.environment,
        session_id: entry.sessionId,
        user_id: entry.userId,
        timestamp: entry.timestamp,
        ...entry.metadata,
      });
    } catch (error) {
      // Fallback to console if Firebase fails
      console.error("Failed to send log to Firebase:", error);
    }
  }

  private async processLog(
    level: keyof LogLevel,
    message: string,
    data?: any,
    metadata?: Record<string, any>
  ) {
    if (!this.shouldLog(level)) return;

    const entry = this.createLogEntry(level, message, data, metadata);

    // Add to buffer for potential export/debugging
    this.addToBuffer(entry);

    // Format and output to console
    const formattedMessage = this.formatLogEntry(entry);

    switch (level) {
      case "DEBUG":
        console.log(formattedMessage, data || "");
        break;
      case "INFO":
        console.info(formattedMessage, data || "");
        break;
      case "WARN":
        console.warn(formattedMessage, data || "");
        break;
      case "ERROR":
        console.error(formattedMessage, data || "");
        break;
    }

    // Send to Firebase in production
    if (isProduction()) {
      await this.sendToFirebase(entry);
    }
  }

  // Public logging methods
  debug(message: string, data?: any, metadata?: Record<string, any>) {
    this.processLog("DEBUG", message, data, metadata);
  }

  info(message: string, data?: any, metadata?: Record<string, any>) {
    this.processLog("INFO", message, data, metadata);
  }

  warn(message: string, data?: any, metadata?: Record<string, any>) {
    this.processLog("WARN", message, data, metadata);
  }

  error(message: string, data?: any, metadata?: Record<string, any>) {
    this.processLog("ERROR", message, data, metadata);
  }

  // Timeseries logging for performance metrics
  time(label: string, metadata?: Record<string, any>) {
    if (!this.shouldLog("DEBUG")) return;

    const startTime = performance.now();
    return {
      end: (additionalData?: any) => {
        const duration = performance.now() - startTime;
        this.debug(
          `⏱️ ${label} completed`,
          {
            duration: `${duration.toFixed(2)}ms`,
            ...additionalData,
          },
          metadata
        );
      },
    };
  }

  // Batch logging for multiple events
  batch(
    entries: Array<{ level: keyof LogLevel; message: string; data?: any }>
  ) {
    entries.forEach(({ level, message, data }) => {
      this.processLog(level, message, data);
    });
  }

  // Export logs for debugging
  exportLogs(): LogEntry[] {
    return [...this.logBuffer];
  }

  // Clear log buffer
  clearLogs(): void {
    this.logBuffer = [];
  }

  // Set user ID for correlation
  setUserId(userId: string): void {
    this.userId = userId;
  }

  // Get current log level
  getLogLevel(): string {
    return (
      Object.keys(LOG_LEVELS).find(
        (key) => LOG_LEVELS[key as keyof LogLevel] === this.currentLevel
      ) || "INFO"
    );
  }

  // Set log level (useful for runtime debugging)
  setLogLevel(level: keyof LogLevel): void {
    this.currentLevel = LOG_LEVELS[level];
    this.info(`Log level changed to ${level}`);
  }
}

// Create singleton instance
export const logger = new FirebaseLogger();

// Export convenience functions
export const debug = (
  message: string,
  data?: any,
  metadata?: Record<string, any>
) => logger.debug(message, data, metadata);

export const info = (
  message: string,
  data?: any,
  metadata?: Record<string, any>
) => logger.info(message, data, metadata);

export const warn = (
  message: string,
  data?: any,
  metadata?: Record<string, any>
) => logger.warn(message, data, metadata);

export const error = (
  message: string,
  data?: any,
  metadata?: Record<string, any>
) => logger.error(message, data, metadata);

export const time = (label: string, metadata?: Record<string, any>) =>
  logger.time(label, metadata);

export const batch = (
  entries: Array<{ level: keyof LogLevel; message: string; data?: any }>
) => logger.batch(entries);

// Export the logger instance for advanced usage
export default logger;
